import React, { useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { PipelineStage } from '../../types';
import { createStage, deleteStage, reorderStages, setInitialStage, updateStage } from '../../services/organizationService';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { ConfirmDialog } from './ConfirmDialog';
interface StageManagerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  stages: PipelineStage[];
}

/** Paleta de colores disponible para los estados. */
export const STAGE_COLORS: {
  value: string;
  label: string;
}[] = [{
  value: '#64748B',
  label: 'Gris'
}, {
  value: '#3D8BFF',
  label: 'Azul'
}, {
  value: '#16A34A',
  label: 'Verde'
}, {
  value: '#DC2626',
  label: 'Rojo'
}, {
  value: '#EAB308',
  label: 'Amarillo'
}, {
  value: '#F97316',
  label: 'Naranja'
}, {
  value: '#7C5CFF',
  label: 'Morado'
}, {
  value: '#EC4899',
  label: 'Rosa'
}];

/**
 * Gestión completa de los estados del embudo: crear, renombrar, recolorear,
 * reordenar, activar, desactivar, eliminar y elegir el estado inicial.
 */
export function StageManager({
  open,
  onClose,
  organizationId,
  stages
}: StageManagerProps) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(STAGE_COLORS[1].value);
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PipelineStage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  /** Nombre en edición: se guarda al salir del campo o con Enter. */
  const [names, setNames] = useState<Record<string, string>>({});
  function nameOf(stage: PipelineStage) {
    return names[stage.id] ?? stage.name;
  }
  async function commitName(stage: PipelineStage) {
    const value = (names[stage.id] ?? '').trim();
    setNames(prev => {
      const next = {
        ...prev
      };
      delete next[stage.id];
      return next;
    });
    if (!value || value === stage.name) return;
    // El identificador del estado no cambia: los leads siguen asociados a él.
    await updateStage(stage.id, {
      name: value
    });
  }
  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    await createStage(organizationId, newName.trim(), newColor);
    setNewName('');
    setCreating(false);
  }
  async function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const ordered = stages.map(s => s.id);
    const from = ordered.indexOf(dragId);
    const to = ordered.indexOf(targetId);
    ordered.splice(to, 0, ordered.splice(from, 1)[0]);
    setDragId(null);
    await reorderStages(organizationId, ordered);
  }
  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    await deleteStage(confirm.id, moveTo || undefined);
    setDeleting(false);
    setConfirm(null);
    setMoveTo('');
  }
  return <>
      <Drawer open={open} onClose={onClose} title="Gestionar estados" data-id="element-253" data-mp="1qkgbwhjehf70">
        <p className="mb-5 text-sm text-muted-foreground" data-id="element-254" data-mp="1qj5agodfgjj2">
          Los cambios se aplican al instante en la tabla de leads, la ficha del
          contacto, los filtros, el embudo y el dashboard.
        </p>

        <ul className="space-y-3" data-id="element-255" data-mp="1zjafirv5f5u2">
          {stages.map(stage => <li key={stage.id} draggable onDragStart={() => setDragId(stage.id)} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(stage.id)} className={`rounded-xl border border-border bg-background p-3 ${dragId === stage.id ? 'opacity-60' : ''}`} data-id="element-256" data-mp="24j6ay2ps5oy0">
              <div className="flex items-center gap-2" data-id="element-257" data-mp="2j9ds8f41x1x4">
                <GripVerticalIcon className="h-4 w-4 flex-none cursor-grab text-muted-foreground" aria-hidden="true" data-id="element-258" data-mp="2jayny823qqwk" />
                <Input id={`stage-${stage.id}`} aria-label="Nombre del estado" value={nameOf(stage)} onChange={e => setNames(prev => ({
              ...prev,
              [stage.id]: e.target.value
            }))} onBlur={() => commitName(stage)} onKeyDown={e => {
              if (e.key === 'Enter') commitName(stage);
            }} data-id="element-259" data-mp="2n8cv7u682olx" />
                <button type="button" aria-label={stage.isInitial ? 'Estado inicial' : 'Marcar como estado inicial'} onClick={() => setInitialStage(organizationId, stage.id)} className={`rounded-md p-2 ${stage.isInitial ? 'text-amber-500' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-260" data-mp="3pq25j0smn30w">
                  <StarIcon className="h-4 w-4" fill={stage.isInitial ? 'currentColor' : 'none'} data-id="element-261" data-mp="3b1xl9sdxejg0" />
                </button>
                <button type="button" aria-label="Eliminar estado" disabled={stages.length <= 1} onClick={() => setConfirm(stage)} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600 disabled:opacity-40" data-id="element-262" data-mp="3619vfa1fo8nd">
                  <Trash2Icon className="h-4 w-4" data-id="element-263" data-mp="le6o4wnmf2cw" />
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pl-6" data-id="element-264" data-mp="tv7dc5x5ao29">
                <div className="flex flex-wrap gap-1.5" data-id="element-265" data-mp="xw12t3q8kax9">
                  {STAGE_COLORS.map(color => <button key={color.value} type="button" aria-label={color.label} aria-pressed={stage.color === color.value} onClick={() => updateStage(stage.id, {
                color: color.value
              })} className={`flex h-7 w-7 items-center justify-center rounded-full transition-transform hover:scale-110 ${stage.color === color.value ? 'ring-2 ring-foreground ring-offset-2' : ''}`} style={{
                backgroundColor: color.value
              }} data-id="element-266" data-mp="jwhti7loutjd">
                      {stage.color === color.value && <CheckIcon className="h-3.5 w-3.5 text-white" data-id="element-267" data-mp="17razrd1s1s46" />}
                    </button>)}
                </div>
                <Switch id={`stage-active-${stage.id}`} label="Activo" checked={stage.active} onChange={active => updateStage(stage.id, {
              active
            })} data-id="element-268" data-mp="1vgn9qf4xi47j" />
              </div>
            </li>)}
        </ul>

        <div className="mt-6 rounded-xl border border-dashed border-border p-4" data-id="element-269" data-mp="256c3662bzemt">
          <Input id="new-stage" label="Nuevo estado" placeholder="Ejemplo: Segunda visita" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-270" data-mp="25499nc1yz3dc" />
          <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-271" data-mp="1w4m9etkz83lv">
            {STAGE_COLORS.map(color => <button key={color.value} type="button" aria-label={color.label} aria-pressed={newColor === color.value} onClick={() => setNewColor(color.value)} className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${newColor === color.value ? 'ring-2 ring-foreground ring-offset-2' : ''}`} style={{
            backgroundColor: color.value
          }} data-id="element-272" data-mp="1r6kpy7jn229v" />)}
          </div>
          <Button className="mt-4 w-full" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-274" data-mp="18fa62acbyprh" />} loading={creating} disabled={!newName.trim()} onClick={handleCreate} data-id="element-273" data-mp="18gbe05y1uw4z">
            Crear estado
          </Button>
        </div>
      </Drawer>

      <ConfirmDialog open={Boolean(confirm)} title={`Eliminar «${confirm?.name ?? ''}»`} description="Ningún lead se borra: elige a qué estado quieres moverlos. Si no eliges ninguno, pasarán al estado inicial." loading={deleting} onCancel={() => {
      setConfirm(null);
      setMoveTo('');
    }} onConfirm={handleDelete} data-id="element-275" data-mp="3f3aeln9ygo4g">
        <Select id="stage-move-to" label="Mover los leads a" placeholder="Estado inicial" options={stages.filter(s => s.id !== confirm?.id).map(s => ({
        value: s.id,
        label: s.name
      }))} value={moveTo} onChange={e => setMoveTo(e.target.value)} data-id="element-276" data-mp="3t1qxecl3e52y" />
      </ConfirmDialog>
    </>;
}